import { createUserAssociation, deleteUserAssociation } from "deso-protocol";
import { useContext, useState } from "react";

import { waitForTxn } from "@/backend/api-helpers";
import { useToast } from "@/components/ui/use-toast";
import { UserContext } from "@/contexts/user-context";
import { AssociationType } from "@/utils/constants";

export const useBlockUser = () => {
  const { user } = useContext(UserContext);
  const { toast } = useToast();

  const [loading, setLoading] = useState(false);

  const blockUser = async (
    blockedUserPublicKey: string,
    onBlocked?: () => void,
  ) => {
    if (!user || !blockedUserPublicKey) {
      return;
    }

    try {
      setLoading(true);

      const data = await createUserAssociation({
        AppPublicKeyBase58Check: process.env.NEXT_PUBLIC_FOCUS_APP_PUBLIC_KEY,
        AssociationType: AssociationType.block,
        TransactorPublicKeyBase58Check: user.publicKey,
        TargetUserPublicKeyBase58Check: blockedUserPublicKey,
        AssociationValue: AssociationType.block,
      });

      const transactionHash = data.submittedTransactionResponse?.TxnHashHex;

      if (!transactionHash) {
        throw new Error("Missing transaction hash.");
      }

      await waitForTxn({
        transactionHash,
      });

      toast({
        title: "Success",
        description: "User has been successfully blocked",
        variant: "success",
      });

      onBlocked?.();
    } catch {
      toast({
        title: "Error",
        description: "An error occurred while blocking user",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const unblockUser = async (
    assosiationId: string,
    onUnblocked?: () => void,
  ) => {
    if (!user) {
      return;
    }

    setLoading(true);

    try {
      const data = await deleteUserAssociation({
        TransactorPublicKeyBase58Check: user?.publicKey,
        AssociationID: assosiationId,
      });

      const transactionHash = data.submittedTransactionResponse?.TxnHashHex;

      if (!transactionHash) {
        throw new Error("Missing transaction hash.");
      }

      await waitForTxn({
        transactionHash,
      });

      toast({
        title: "Success",
        description: "User has been successfully unblocked",
        variant: "success",
      });

      onUnblocked?.();
    } catch {
      toast({
        title: "Error",
        description: "An error occurred while unblocking user",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    blockUser,
    unblockUser,
  };
};
